import type { LocalAudioRecordingTranscriptTurnRecord } from "@/domains/dexie-db/models/audio-recording";

const nextOffsetForGroup = (group: { transcriptTurnIndex: number }[]): number => {
  if (!group.length) return 0;

  return Math.max(...group.map(turn => turn.transcriptTurnIndex)) + 1;
};

/**
 * Combines finalized transcript turn groups from sequential handoff segments.
 *
 * Each recording starts turn indexes at zero. When we render or transform a
 * handoff session, earlier segment turns need an offset so later segment turns
 * do not overwrite them in transcript projections keyed by turn order.
 */
export function combineSequentialTranscriptTurnGroups(
  groups: LocalAudioRecordingTranscriptTurnRecord[][]
): LocalAudioRecordingTranscriptTurnRecord[] {
  let offset = 0;

  return groups.flatMap(group => {
    const orderedGroup = [...group].sort((a, b) => a.transcriptTurnIndex - b.transcriptTurnIndex);
    const shiftedGroup = orderedGroup.map(turn => ({
      ...turn,
      transcriptTurnIndex: turn.transcriptTurnIndex + offset,
      content: {
        ...turn.content,
        turnOrder: turn.content.turnOrder + offset,
      },
    }));

    offset += nextOffsetForGroup(orderedGroup);
    return shiftedGroup;
  });
}

/**
 * Shifts finalized turns for a later handoff segment by a caller-selected
 * display offset.
 */
export function offsetTranscriptTurns({
  transcriptTurns,
  finalizedTurnOffset,
}: {
  transcriptTurns: LocalAudioRecordingTranscriptTurnRecord[];
  finalizedTurnOffset: number;
}): LocalAudioRecordingTranscriptTurnRecord[] {
  if (finalizedTurnOffset === 0) return transcriptTurns;

  return transcriptTurns.map(turn => ({
    ...turn,
    transcriptTurnIndex: turn.transcriptTurnIndex + finalizedTurnOffset,
    content: {
      ...turn.content,
      turnOrder: turn.content.turnOrder + finalizedTurnOffset,
    },
  }));
}

/**
 * Shifts live partial turns so they render after finalized predecessor turns.
 */
export function offsetLiveTranscriptPartials<T extends { turnOrder: number }>({
  liveTranscriptPartials,
  finalizedTurnOffset,
}: {
  liveTranscriptPartials: T[];
  finalizedTurnOffset: number;
}): T[] {
  if (finalizedTurnOffset === 0) return liveTranscriptPartials;

  return liveTranscriptPartials.map(
    partialTurn =>
      ({
        ...partialTurn,
        turnOrder: partialTurn.turnOrder + finalizedTurnOffset,
      }) as T
  );
}

/**
 * Returns the turn index the next sequential recording segment should start at.
 */
export function getNextTranscriptTurnOffset(
  turns: LocalAudioRecordingTranscriptTurnRecord[]
): number {
  return nextOffsetForGroup(turns);
}

/**
 * Returns the next display turn index after finalized turns and any still-live
 * partials from the same handoff segment.
 */
export function getNextTranscriptDisplayTurnOffset({
  finalizedTurns,
  livePartialTurns,
}: {
  finalizedTurns: LocalAudioRecordingTranscriptTurnRecord[];
  livePartialTurns: { turnOrder: number }[];
}): number {
  const finalizedOffset = getNextTranscriptTurnOffset(finalizedTurns);
  const liveOffset = livePartialTurns.length
    ? Math.max(...livePartialTurns.map(turn => turn.turnOrder)) + 1
    : 0;

  return Math.max(finalizedOffset, liveOffset);
}
